import {
  Button,
  HStack,
  Stack,
  VStack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { HookFormDropdown } from '$/components/core/Form/HookFormDropdown';
import { HookFormInput } from '$/components/core/Form/HookFormInput';
import { useToast } from '$/hooks/useToast';
import { DeleteSchoolModal } from '$/pages/SchoolPage/components/DeleteSchoolModal';
import { type School } from '$/services/usecases/school/mapper/schools';
import { schoolTeachersQuery } from '$/services/usecases/school/queries';
import { updateSchool } from '$/services/usecases/school/schools';

interface Props {
  school: School;
  isOpen: boolean;
  onClose: () => void;
}

type FormData = {
  name: string;
  department: string;
  address: string;
  website: string;
  phone: string;
  deputyId: string | null;
  deputyQuery: string;
};

export const EditSchoolModal: FC<Props> = ({ school, isOpen, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const deputyDropdown = useDisclosure();
  const deleteModal = useDisclosure();
  const user = useAuthenticationStore.useUser();
  const canChangeDeputy =
    user?.role === 'superTeacher' && user.id === school.requesterId;

  const { data: teachers } = useQuery(schoolTeachersQuery(school.id));

  const { deputyOptions, deputyOptionLabels } = useMemo(
    () => ({
      deputyOptions:
        teachers?.response.payload.filter((t) => t.id !== school.requesterId) ??
        [],
      deputyOptionLabels:
        teachers?.response.payload.reduce(
          (acc, deputy) => {
            acc[deputy.id] = `${deputy.firstname} ${deputy.lastname}`;
            return acc;
          },
          {} as Record<string, string>,
        ) ?? {},
    }),
    [teachers, school],
  );

  const formMethods = useForm<FormData>({ mode: 'onChange' });

  useEffect(() => {
    formMethods.reset({
      name: school.name,
      department: school.department,
      address: school.address,
      website: school.website,
      phone: school.phone,
      deputyId: school.deputyId,
    });
  }, [school, formMethods]);

  const { mutate, isPending } = useMutation({
    mutationFn: updateSchool,
    onSuccess: async (data) => {
      if (!data.isSuccessful) {
        toast(t('general.error'), 'error');
        return;
      }

      toast(t('school.profile.editSuccessTitle'), 'success');
      onClose();
    },
    onError: () => {
      toast(t('general.error'), 'error');
    },
  });

  const onSubmit = (data: FormData) => {
    mutate({
      id: school.id,
      name: data.name,
      department: data.department,
      address: data.address,
      website: data.website,
      phone: data.phone,
      ...(canChangeDeputy && { deputyId: data.deputyId }),
    });
  };

  const onCloseModal = () => {
    onClose();
    formMethods.reset();
  };

  return (
    <>
      <FullModal
        isOpen={isOpen && !deleteModal.isOpen}
        onClose={onCloseModal}
        header={t('school.profile.edit')}
        isScrollable={false}
      >
        <FormProvider {...formMethods}>
          <VStack
            as='form'
            gap='4'
            onSubmit={formMethods.handleSubmit(onSubmit)}
          >
            <HStack gap='4' w='full'>
              <HookFormInput<FormData>
                accessor='name'
                type='text'
                label={t('school.registerSchool.form.schoolName')}
                registerOptions={{
                  required: t(
                    'dashboard.authentication.errorMessages.isRequired',
                  ),
                }}
              />
              <HookFormInput<FormData>
                accessor='department'
                type='text'
                label={t('school.registerSchool.form.department')}
                registerOptions={{
                  required: t(
                    'dashboard.authentication.errorMessages.isRequired',
                  ),
                }}
              />
            </HStack>
            <HookFormInput<FormData>
              accessor='address'
              type='text'
              label={t('school.registerSchool.form.fullAddress')}
              registerOptions={{
                required: t(
                  'dashboard.authentication.errorMessages.isRequired',
                ),
              }}
            />
            <HStack gap='4' w='full'>
              <HookFormInput<FormData>
                accessor='website'
                type='text'
                label={t('school.registerSchool.form.website')}
              />
              <HookFormInput<FormData>
                accessor='phone'
                type='text'
                label={t('school.registerSchool.form.phone')}
              />
            </HStack>
            <HookFormDropdown<FormData>
              dropdownOpen={deputyDropdown.isOpen}
              toggleDropdown={deputyDropdown.onToggle}
              isDisabled={!canChangeDeputy}
              accessor='deputyId'
              placeholder={t('school.profile.deputyPlaceholder')}
              label={t('school.profile.deputy')}
              valueLabels={deputyOptionLabels}
              onBlur={deputyDropdown.onClose}
              dropdownContent={() => {
                return (
                  <Stack pt='2' pb='6px'>
                    {deputyOptions?.length === 0 && (
                      <Text px='4' py='6px' color='lighterText' fontSize='sm'>
                        {t('school.profile.noDeputyAvailable')}
                      </Text>
                    )}
                    {deputyOptions?.map((deputy) => (
                      <Button
                        key={deputy.id}
                        px='4'
                        py='6px'
                        fontSize='sm'
                        fontWeight='normal'
                        textAlign='left'
                        _hover={{ bg: 'bodyBackground' }}
                        cursor='pointer'
                        onClick={() => {
                          formMethods.setValue('deputyId', deputy.id, {
                            shouldDirty: true,
                          });
                          deputyDropdown.onClose();
                        }}
                        type='button'
                        variant='unstyled'
                      >
                        <HStack>
                          <Text>
                            {deputy.firstname} {deputy.lastname}
                          </Text>
                        </HStack>
                      </Button>
                    ))}
                  </Stack>
                );
              }}
            />
            <HStack justify='space-between' w='full' mt='4'>
              <Button
                isDisabled={isPending}
                onClick={onCloseModal}
                type='button'
                variant='ghost'
              >
                {t('general.cancel')}
              </Button>
              <Button
                isDisabled={
                  !formMethods.formState.isValid ||
                  !formMethods.formState.isDirty
                }
                isLoading={isPending}
                type='submit'
              >
                {t('general.save')}
              </Button>
            </HStack>
            <Button
              isDisabled={isPending}
              onClick={deleteModal.onOpen}
              type='button'
              variant='danger'
            >
              {t('school.profile.deleteSchool')}
            </Button>
          </VStack>
        </FormProvider>
      </FullModal>
      <DeleteSchoolModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        school={school}
        onSuccess={() => {
          deleteModal.onClose();
          onClose();
          void navigate({ to: '/' });
        }}
      />
    </>
  );
};
